import { useEffect, useState } from "react";
import { api } from "../../api/client";
import { Button, Icon } from "../../components/ui";

// ─── Kunci payroll BERSAMA (gaji trainer + staff) ────────────────────────────
//
// Satu kunci per cabang per bulan. Dipasang dari Trainer Payroll, Salary
// Adjustments, atau Staff Payroll — ketiganya memanggil kunci yang SAMA di server,
// jadi mengunci di satu halaman ikut membekukan yang lain.

const NAMA_BULAN = [
  "Januari", "Februari", "Maret", "April", "Mei", "Juni",
  "Juli", "Agustus", "September", "Oktober", "November", "Desember"
];

export const labelBulan = (year, month) => `${NAMA_BULAN[Number(month) - 1] || month} ${year}`;

function waktu(v) {
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString("id-ID", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  });
}

// Status kunci untuk satu bulan di cabang aktif. Gagal dimuat (mis. mode "Semua
// Cabang" — kunci berlaku per cabang) = tak ada informasi: halamannya tetap
// tampil, hanya tanpa bilah kunci.
export function useKunciPayroll(year, month) {
  const [kunci, setKunci] = useState(null);
  const [versi, setVersi] = useState(0);

  useEffect(() => {
    let batal = false;
    (async () => {
      if (!year || !month) {
        setKunci(null);
        return;
      }
      try {
        const r = await api.getPayrollLock({ year, month });
        if (!batal) setKunci(r?.success ? r : null);
      } catch {
        if (!batal) setKunci(null);
      }
    })();
    return () => {
      batal = true;
    };
  }, [year, month, versi]);

  return { kunci, muat: () => setVersi((v) => v + 1) };
}

export function KunciPayrollBar({ kunci, year, month, bisaKunci, bisaBuka, setToast, onBerubah }) {
  const [sibuk, setSibuk] = useState(false);
  if (!kunci) return null;
  const label = labelBulan(year, month);

  async function jalankan(aksi, tanya) {
    if (!window.confirm(tanya)) return;
    setSibuk(true);
    try {
      const r = await aksi();
      setToast({ type: r?.success ? "success" : "error", message: r?.message || "Selesai." });
    } catch (err) {
      setToast({ type: "error", message: err.message });
    } finally {
      setSibuk(false);
      if (onBerubah) onBerubah();
    }
  }

  if (kunci.terkunci) {
    return (
      <div className="anim-fade mb-5 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-slate-300 bg-slate-100 px-4 py-3 text-sm dark:border-slate-700 dark:bg-slate-800/60">
        <div className="min-w-0">
          <p className="font-black">
            <Icon name="lock" className="mr-1 align-middle text-base" />
            Payroll {label} sudah dikunci
            {kunci.dikunciOleh ? ` oleh ${kunci.dikunciOleh}` : ""}
            {kunci.dikunciPada ? ` · ${waktu(kunci.dikunciPada)}` : ""}
          </p>
          <p className="mt-0.5 text-xs text-slate-500">
            Gaji trainer & staff bulan ini beku. Absensi, reset, permintaan, bracket, serta
            insentif/potongannya tak bisa diubah lagi.
          </p>
        </div>
        {bisaBuka ? (
          <Button
            variant="secondary"
            disabled={sibuk}
            onClick={() =>
              jalankan(
                () => api.unlockPayroll({ year, month }),
                `Buka kembali payroll ${label}? Gaji trainer & staff bulan ini bisa berubah lagi, `
                  + "padahal mungkin sudah dibayarkan."
              )
            }
          >
            <Icon name="lock_open" className="text-base" />
            Buka Kunci
          </Button>
        ) : (
          <span className="text-xs text-slate-500">Hanya super admin yang bisa membukanya.</span>
        )}
      </div>
    );
  }

  // Bulan berjalan belum bisa dikunci (masih diabsen setiap hari) — server
  // menolaknya, jadi tombolnya tak ditawarkan sama sekali.
  if (!kunci.bulanSelesai || !bisaKunci) return null;

  return (
    <div className="anim-fade mb-5 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-primary/20 bg-primary/5 px-4 py-3 text-sm">
      <p className="min-w-0 text-xs text-slate-600 dark:text-slate-300">
        {label} sudah berakhir. Kunci payroll-nya setelah semua angka benar — gaji trainer &
        staff bulan ini lalu dibekukan.
      </p>
      <Button
        disabled={sibuk}
        onClick={() =>
          jalankan(
            () => api.lockPayroll({ year, month }),
            `Kunci payroll ${label}?\n\nGaji trainer dihitung ulang sekali lagi, lalu gaji trainer `
              + "& staff bulan ini dibekukan. Sesudahnya absensi, reset, permintaan, bracket, serta "
              + "insentif/potongan bulan ini tak bisa diubah. Hanya super admin yang bisa membukanya."
          )
        }
      >
        <Icon name="lock" className="text-base" />
        Kunci Bulan
      </Button>
    </div>
  );
}
